import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { groupsApi, GroupResponse } from '../lib/groups';
import { sitesApi } from '../lib/sites';
import { useAuth } from '../hooks/useAuth';
import GroupsList from '../components/groups/GroupsList';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { canManageGroups } from '../utils/role-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function GroupsPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  const hasPermission = canManageGroups(currentUser?.role);
  const isSuperUser = currentUser?.role === 'super_user';
  const isSiteAdmin = currentUser?.role === 'site_admin';

  // Get page title based on role
  const getPageTitle = () => {
    if (isSuperUser) return 'Groups';
    if (isSiteAdmin) return 'User Groups';
    return 'Groups';
  };

  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.getAll(),
    enabled: hasPermission,
  });

  const { data: allGroups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupsApi.getAll(),
    enabled: hasPermission,
  });

  // Filter groups for site admins - only show groups from their assigned sites
  const groups = isSiteAdmin && sites
    ? allGroups?.filter((group) => sites.some((site) => site.id === group.siteId))
    : allGroups;


  const deleteMutation = useMutation({
    mutationFn: (id: string) => groupsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });

  const handleCreate = () => {
    navigate('/groups/new');
  };

  const handleEdit = (group: GroupResponse) => {
    navigate(`/groups/${group.id}`);
  };

  const handleDelete = (id: string) => {
    setGroupToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (groupToDelete) {
      deleteMutation.mutate(groupToDelete);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  if (!hasPermission) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>You do not have permission to access this page.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoadingGroups) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="px-2 py-3 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">{getPageTitle()}</h1>
        <Button onClick={handleCreate} className="self-start sm:self-auto">
          <Users className="h-4 w-4 mr-2" />
          Add Group
        </Button>
      </div>

      {isSiteAdmin && (
        <Alert className="mb-4">
          <AlertDescription>
            You can manage user groups for your assigned sites. Groups control which presets end users can access.
          </AlertDescription>
        </Alert>
      )}

      <GroupsList
        groups={groups || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
